/* Server-rendered dates plus a checkout barrier for enrolled cart lines. */
(() => {
  'use strict';
  if (window.rossiFulfillmentInstalled) return;
  window.rossiFulfillmentInstalled = true;
  const names = {date: 'Estimated to ship', restock: 'Supplier restock ETA', policy: '_Rossi fulfillment policy', expires: '_Rossi fulfillment expires'};
  const legacyDateName = 'Expected ship date';
  const root = window.Shopify?.routes?.root || '/';
  const current = () => document.querySelector('form[data-product-form] [name="id"]')?.value;
  const cardFor = (variant) => Array.from(document.querySelectorAll('[data-rossi-fulfillment]'))
    .find(card => card.dataset.variantId === String(variant) && card.closest('[data-rossi-stock-variant]'));
  const valid = card => card && card.dataset.availability === 'BACKORDER'
    && Date.parse(card.dataset.expiresAt) > Date.now() && Date.parse(card.dataset.evaluatedAt) <= Date.now()
    && Date.parse(card.dataset.expectedDispatch) > Date.now();

  function updateProperties(form, card) {
    form.querySelectorAll('[data-rossi-property]').forEach(input => input.remove());
    if (!valid(card)) return;
    const values = {date: card.dataset.shipLabel, restock: card.dataset.restockLabel, policy: card.dataset.policyVersion, expires: card.dataset.expiresAt};
    Object.entries(names).forEach(([kind, name]) => {
      if (!values[kind]) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = values[kind];
      input.dataset.rossiProperty = kind;
      form.append(input);
    });
  }

  function clearSelectedSchema() {
    const script = document.querySelector('script[data-rossi-product-schema]');
    if (!script) return;
    try {
      const data = JSON.parse(script.textContent);
      const offers = Array.isArray(data.offers) ? data.offers : [data.offers];
      offers.filter(Boolean).forEach(offer => { delete offer.availability; delete offer.availabilityStarts; });
      script.textContent = JSON.stringify(data);
    } catch (_) { script.textContent = ''; }
  }

  function expire() {
    document.querySelectorAll('[data-rossi-fulfillment]').forEach(card => {
      if (Date.parse(card.dataset.expiresAt) > Date.now()) return;
      card.dataset.availability = '';
      card.dataset.expectedDispatch = '';
      card.textContent = 'Availability is being confirmed. Shipping date unavailable.';
      if (card.dataset.variantId === current()) clearSelectedSchema();
    });
    document.querySelectorAll('form[data-product-form]').forEach(form => updateProperties(form, cardFor(form.querySelector('[name="id"]')?.value)));
  }

  let selected = current(), version = 0;
  async function refreshVariant() {
    if (!document.querySelector('[data-rossi-stock-variant] [data-rossi-fulfillment]')) return;
    const variant = current();
    if (!variant || variant === selected) return;
    selected = variant;
    const request = ++version;
    document.querySelectorAll('form[data-product-form]').forEach(form => updateProperties(form, cardFor(form.querySelector('[name="id"]')?.value)));
    // Remove the previous variant's claim immediately while its SSR replacement loads.
    clearSelectedSchema();
    document.querySelectorAll('[data-rossi-legacy-fulfillment]').forEach(node => { node.hidden = true; });
    const url = new URL(location.href);
    url.searchParams.set('variant', variant);
    try {
      const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store'});
      if (!response.ok) return;
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (request !== version || current() !== variant) return;
      const schema = page.querySelector('script[data-rossi-product-schema]');
      const existing = document.querySelector('script[data-rossi-product-schema]');
      if (schema && existing) existing.textContent = schema.textContent;
      document.querySelectorAll('[data-rossi-legacy-fulfillment]').forEach(node => {
        const next = Array.from(page.querySelectorAll('[data-rossi-legacy-fulfillment]'))
          .find(next => next.dataset.rossiLegacyFulfillment === node.dataset.rossiLegacyFulfillment);
        if (next) { node.innerHTML = next.innerHTML; node.hidden = next.hidden; }
      });
      document.querySelectorAll('[data-rossi-stock-variant]').forEach(slot => {
        if (slot.dataset.rossiStockVariant !== variant) return;
        const next = Array.from(page.querySelectorAll('[data-rossi-stock-variant]')).find(node => node.dataset.rossiStockVariant === variant);
        if (next) slot.innerHTML = next.innerHTML;
      });
      expire();
    } catch (_) { /* Keep no claim if refresh fails. */ }
  }

  let cartFlight = null;
  function refreshCart() {
    if (cartFlight) return cartFlight;
    cartFlight = synchronizeCart().finally(() => { cartFlight = null; });
    return cartFlight;
  }
  async function synchronizeCart() {
    try {
      const response = await fetch(`${root}cart?section_id=rossi-cart-fulfillment`, {credentials: 'same-origin', cache: 'no-store'});
      if (!response.ok) throw new Error('cart section unavailable');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = page.querySelector('[data-rossi-cart-fulfillment]');
      if (!fresh) throw new Error('cart section missing');
      document.querySelectorAll('[data-rossi-cart-fulfillment]').forEach(node => { node.innerHTML = fresh.innerHTML; });
      const responseCart = await fetch(`${root}cart.js`, {credentials: 'same-origin', cache: 'no-store'});
      if (!responseCart.ok) throw new Error('cart unavailable');
      const cart = await responseCart.json();
      for (const item of cart.items || []) {
        const row = Array.from(fresh.querySelectorAll('[data-rossi-cart-line]')).find(node => node.dataset.rossiCartLine === item.key);
        const card = row?.querySelector('[data-rossi-fulfillment]');
        const before = item.properties || {};
        // Only service-enrolled variants or already service-owned properties are touched.
        if (!row && !Object.hasOwn(before, names.policy)) continue;
        const properties = {...before};
        Object.values(names).forEach(name => delete properties[name]);
        // Migrate only enrolled/service-owned cart lines from the former label.
        delete properties[legacyDateName];
        if (valid(card)) {
          properties[names.date] = card.dataset.shipLabel;
          if (card.dataset.restockLabel) properties[names.restock] = card.dataset.restockLabel;
          properties[names.policy] = card.dataset.policyVersion;
          properties[names.expires] = card.dataset.expiresAt;
        }
        if (JSON.stringify(properties) !== JSON.stringify(before)) {
          const changed = await fetch(`${root}cart/change.js`, {method: 'POST', credentials: 'same-origin',
            headers: {'Content-Type': 'application/json'}, body: JSON.stringify({id: item.key, properties})});
          if (!changed.ok) throw new Error('cart properties update failed');
          const updated = await changed.json();
          const sameProperties = candidate => Object.keys(candidate).length === Object.keys(properties).length
            && Object.entries(properties).every(([key, value]) => candidate[key] === value);
          const next = updated.items?.find(line => line.variant_id === item.variant_id
            && sameProperties(line.properties || {}));
          if (!next) throw new Error('updated cart line missing');
          // Shopify changes line keys when properties change. Keep the theme's
          // quantity controls and notice selectors attached to the new key.
          [document, fresh].forEach(scope => {
            ['data-line-item-key', 'data-rossi-cart-line', 'data-rossi-cart-notice'].forEach(attribute => {
              scope.querySelectorAll(`[${attribute}]`).forEach(node => {
                if (node.getAttribute(attribute) === item.key) node.setAttribute(attribute, next.key);
              });
            });
          });
        }
      }
      // Main-cart per-line notices also use the newly rendered contract.
      document.querySelectorAll('[data-rossi-cart-notice]').forEach(node => {
        const row = Array.from(fresh.querySelectorAll('[data-rossi-cart-line]')).find(row => row.dataset.rossiCartLine === node.dataset.rossiCartNotice);
        node.innerHTML = row?.querySelector('[data-rossi-fulfillment]')?.outerHTML || '';
      });
      // A managed zero-stock line must have a current shipping commitment.
      return Array.from(fresh.querySelectorAll('[data-rossi-fulfillment]')).every(card =>
        card.dataset.availability === 'IN_STOCK' || valid(card));
    } catch (_) {
      document.querySelectorAll('[data-rossi-cart-fulfillment], [data-rossi-cart-notice]').forEach(node => {
        if (node.querySelector('[data-rossi-fulfillment]')) node.textContent = 'Shipping date unavailable. Please confirm before ordering.';
      });
      return false;
    }
  }

  let checkoutBusy = false;
  async function checkout(event, button) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy) return;
    checkoutBusy = true;
    button?.setAttribute('aria-busy', 'true');
    try {
      // Finish any older read first, then read again: a just-added line can
      // arrive while the previous section/cart request was in flight.
      if (cartFlight) await cartFlight;
      if (!await refreshCart()) {
        let message = document.querySelector('[data-rossi-checkout-error]');
        if (!message) {
          message = document.createElement('p');
          message.dataset.rossiCheckoutError = '';
          message.setAttribute('role', 'alert');
          (button?.parentElement || document.body).append(message);
        }
        message.textContent = 'We could not confirm the shipping date. Please refresh and try again, or contact us.';
        return;
      }
      // Shopify handles market-aware checkout from the cart POST. Do not
      // re-submit stale DOM line keys after changing item properties.
      const form = document.createElement('form');
      form.action = `${root}cart`;
      form.method = 'post';
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = 'checkout'; input.value = 'Checkout';
      form.append(input); document.body.append(form);
      HTMLFormElement.prototype.submit.call(form);
    } finally {
      checkoutBusy = false;
      button?.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[name="checkout"], a[href="/checkout"]');
    if (button) checkout(event, button);
  }, true);

  document.addEventListener('change', () => setTimeout(refreshVariant, 0));
  ['variant:change', 'rio:variant-change', 'product:variant-change', 'product:variant:change'].forEach(name =>
    document.addEventListener(name, () => setTimeout(refreshVariant, 0)));
  ['flu:cart:updated', 'cart:updated', 'cart:change'].forEach(name => document.addEventListener(name, () => refreshCart()));
  document.addEventListener('submit', event => {
    if (event.submitter?.name === 'checkout') {
      checkout(event, event.submitter);
      return;
    }
    if (event.target.matches('form[data-product-form]')) {
      updateProperties(event.target, cardFor(event.target.querySelector('[name="id"]')?.value));
      setTimeout(refreshCart, 1500);
    }
  }, true);
  document.addEventListener('click', event => {
    if (event.target.closest('[aria-controls="cart-flyout-drawer"], .js-cart-open, [data-quick-cart] button')) setTimeout(refreshCart, 500);
  });
  // Existing theme sets hidden variant fields and URL without dispatching change.
  setInterval(refreshVariant, 500);
  setInterval(expire, 1000);
  setInterval(() => { if (document.querySelector('[data-rossi-cart-fulfillment], [data-rossi-cart-notice]')) refreshCart(); }, 30000);
  expire();
  if (document.querySelector('[data-rossi-cart-fulfillment], [data-rossi-cart-notice]')) refreshCart();
})();
